/**
 * S6 — the confirm. **The gesture CLAUDE.md rule 3 is written in.**
 *
 * Rule 3 says nothing posts to Business Central automatically, ever: posting is
 * always an explicit manager action behind the review screen. That is easy to honour
 * in prose and easy to lose to a stray request, so it is made structural here —
 * **`confirm: true` is written in this file and nowhere else in the app** (a contract
 * test keeps it that way), the backend answers 422 without it, and the only thing
 * that can produce it is a tap on the button below.
 *
 * What the modal names is chosen so a manager can catch a wrong count *before* it
 * moves inventory rather than after: the area, the posting date the deltas were
 * computed against, how many lines will post, and what the whole count is worth. All
 * four are strings the server computed — this dialog adds nothing up.
 *
 * **`expected_line_count` closes a real gap** (Milestone C's recommendation). Staging
 * is shared and a count can be resumed on a second iPad, so a line can appear or
 * vanish between this screen loading and the tap. The server refuses a mismatch, and
 * the point of a review is that what posts is what was reviewed.
 *
 * ⚠ **The failure branches are the substance of this file, not an afterthought.**
 * § 4.5: the transport fault breaks the *reply*, not the request, so a post whose
 * answer never arrives may well have posted.
 *
 * - **A clean 4xx is an answer.** The server refused deliberately — not `InReview`,
 *   the line count moved, not the owner — and nothing reached the posting codeunit.
 *   The marker is cleared and the refusal is shown right here, because sending a
 *   manager to a verifying screen that will poll forever over a 409 helps nobody.
 * - **Everything else is not an answer.** A transport failure, a timeout, a 502 (the
 *   backend's own translation of a BC call that broke) — any of those can sit on
 *   either side of a post that actually happened. The marker stays, and the screen
 *   moves to verifying, which asks Business Central and offers no way to send
 *   anything.
 *
 * There is **no retry button** in either branch and no retry in the mutation. A
 * refusal is fixed by fixing what it names and confirming again, deliberately.
 *
 * ⚠ **`neverStarted` is the 2026-08-24 ruling, and it is not a retry.** The
 * verifying screen renders this same component once Business Central has read
 * `InReview` on two consecutive polls more than a minute after the attempt — which
 * means the posting codeunit never took the first request, because it marks a
 * session `Posting` as its first act (`posting/neverStarted.ts`). Nothing is
 * re-sent: the lost request is gone, and what happens here is a **new post**, made
 * by a manager who has just been told in as many words that the first one never
 * arrived, over an `expected_line_count` read live a moment ago. `PostSession`'s own
 * `InReview` guard is still underneath it, so a first request that did land refuses
 * this one with a 409.
 *
 * That branch changes exactly one thing about the failure handling, and it is worth
 * saying why: **a 4xx does not clear the marker here.** On S5 clearing it is right —
 * the post never happened and the manager stays where they are. On the verifying
 * screen the marker is what keeps that screen rendered, so clearing it would throw
 * the manager off the screen *and* take the server's refusal with them, mid-sentence.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError } from "../api/client";
import { usePostSession } from "../api/queries";
import type { CountSession, SessionReview } from "../api/types";
import { announceSessionRecord, clearPostAttempt } from "../offline/draftStore";

/**
 * The one status `PostSession` will post from. Mirrors `count/posting.py`
 * `POSTABLE_STATUS`, so the button is never a promise the server refuses.
 */
const POSTABLE = ["InReview"];

export function ConfirmPost({
  session,
  areaId,
  mine,
  mayPost = false,
  review,
  neverStarted = false,
}: {
  session: CountSession;
  areaId: string;
  mine: boolean;
  /**
   * The server's `may_post` for a session that is **not** the caller's - the
   * reviewer exception. The confirm is offered to the owner or to a caller the
   * server has already said may post it; everyone else gets no button, and a
   * request that got past this anyway is refused by the server in its own words.
   */
  mayPost?: boolean;
  review: SessionReview;
  /**
   * Rendered as the **re-offer** after a post that never reached Business Central
   * (the 2026-08-24 ruling). Changes the words — a manager must know which of the
   * two situations they are in — and keeps the post marker across a refusal.
   */
  neverStarted?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const post = usePostSession(areaId, session);
  const navigate = useNavigate();
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    panel.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      // Escape cannot close it mid-post: the request is already gone, and a dialog
      // that vanished would leave a manager with no idea whether it went.
      if (event.key === "Escape" && !post.isPending) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, post.isPending]);

  if (!(mine || mayPost) || !POSTABLE.includes(session.status)) return null;

  const postingPath = `/areas/${encodeURIComponent(areaId)}/sessions/${encodeURIComponent(
    session.session_id,
  )}/posting`;
  const lineCount = review.totals.line_count;
  const blocking = review.advisories.filter((a) => a.expect_al_refusal);

  const send = async () => {
    setRefusal(null);
    try {
      // The one place in this app that says `confirm: true`, and it says it because
      // a manager tapped this button.
      await post.mutateAsync({ confirm: true, expected_line_count: lineCount });
      navigate(postingPath);
    } catch (err) {
      const refused = err instanceof ApiError && err.status >= 400 && err.status < 500;
      if (refused) {
        // An answer: the server decided, before Business Central was asked to post
        // anything. Safe to forget the attempt — and necessary, or the next visit
        // would land on verifying over a post that never happened.
        //
        // **Except from the verifying screen**, where the marker is the reason that
        // screen is rendered at all. Clearing it there would redirect the manager
        // away the instant the refusal appeared, which is the one moment they need to
        // read it — and the honest description of that session is unchanged anyway:
        // a post was attempted, and Business Central has given no terminal outcome.
        if (!neverStarted) await clearPostAttempt(session.session_id);
        setRefusal((err as ApiError).detail);
        return;
      }
      // Not an answer. The marker written before the send stands, and *now* is when
      // routing needs to know about it — the reply is lost, so the only honest screen
      // is the one that asks Business Central and offers no way to send anything.
      announceSessionRecord(session.session_id);
      navigate(postingPath);
    }
  };

  return (
    <div className="card" data-testid="post-panel">
      {neverStarted ? (
        <>
          <h3>Post this count — the first attempt never reached Business Central</h3>
          <p className="small">
            Nothing was posted by the attempt that lost its reply, and nothing is being
            re-sent: that request is gone. This posts the count for the first time.
          </p>
        </>
      ) : (
        <>
          <h3>Post this count to Business Central</h3>
          <p className="small">
            Posting stages nothing further: it hands this count to Business Central,
            which posts the item journal and moves the inventory. It is the last step.
          </p>
        </>
      )}
      <button
        type="button"
        className="primary"
        data-testid="open-confirm-post"
        onClick={() => setOpen(true)}
      >
        {neverStarted ? "Post this count now…" : "Post this count…"}
      </button>

      {open && (
        <div className="sheet-backdrop" data-testid="confirm-post-backdrop">
          <div
            className="sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Confirm posting this count"
            tabIndex={-1}
            ref={panel}
            data-testid="confirm-post"
          >
            <h3>Post this count to Business Central?</h3>
            {neverStarted && (
              /* Said before the figures, because it is the thing this manager does not
                 already know: they watched a post go and heard nothing back, and the
                 app has since established that Business Central never got it. */
              <p className="small" data-testid="confirm-never-started">
                <strong>The earlier attempt never reached Business Central.</strong> It
                is still showing this count as in review, and it marks a count as
                posting the moment a request arrives — so nothing has posted, and
                nothing is being re-sent. This is the first post of this count.
              </p>
            )}
            <p className="small">
              This moves inventory. It cannot be undone from this app — a wrong post is
              corrected by posting another count, not by cancelling this one.
            </p>

            <dl className="facts" data-testid="confirm-facts">
              <div>
                <dt>Area</dt>
                <dd>
                  {review.area.display_name} · {review.area.location_code}
                </dd>
              </div>
              <div>
                <dt>Posting date</dt>
                {/* The date the deltas were computed against — the basis of every
                    figure the manager just reviewed. */}
                <dd data-testid="confirm-posting-date">
                  <strong>{review.as_of}</strong>
                </dd>
              </div>
              <div>
                <dt>Lines that will post</dt>
                <dd data-testid="confirm-line-count">{lineCount}</dd>
              </div>
              <div>
                <dt>Inventory value counted</dt>
                <dd className="big" data-testid="confirm-counted-value">
                  ${review.totals.counted_value}
                </dd>
              </div>
              <div>
                <dt>Value of the adjustment</dt>
                <dd className="big" data-testid="confirm-delta-value">
                  ${review.totals.delta_value}
                </dd>
              </div>
            </dl>

            {review.totals.flagged_lines > 0 && (
              <p className="small" data-testid="confirm-flagged">
                {review.totals.flagged_lines} line
                {review.totals.flagged_lines === 1 ? " is" : "s are"} flagged as a major
                swing. <strong>Flags never block posting</strong> — they mark what to
                look at twice, and you have.
              </p>
            )}

            {review.uncounted_item_count > 0 && (
              <p className="small" data-testid="confirm-uncounted">
                {review.uncounted_item_count} item
                {review.uncounted_item_count === 1 ? "" : "s"} on this area's list{" "}
                {review.uncounted_item_count === 1 ? "was" : "were"} left blank. Business
                Central will leave {review.uncounted_item_count === 1 ? "its" : "their"}{" "}
                inventory unchanged — blank is never counted as zero.
              </p>
            )}

            {review.nothing_to_post && (
              <p className="small" data-testid="confirm-nothing-to-post">
                Every counted item already matches Business Central, so this post will
                move no inventory. It still records that the count happened.
              </p>
            )}

            {blocking.length > 0 && (
              <div className="card error" data-testid="confirm-blocking">
                <p>
                  <strong>
                    Business Central is expected to refuse this post
                    {blocking.length === 1 ? "" : ` for ${blocking.length} reasons`}:
                  </strong>
                </p>
                {blocking.map((advisory) => (
                  <p key={advisory.code + (advisory.item_no ?? "")} className="small">
                    {advisory.message}
                  </p>
                ))}
                <p className="muted small">
                  {/* The app is guessing at the AL's verdict and must not sound like
                      it is issuing one — nor block a post the AL might well accept. */}
                  Business Central checks all of this again and has the final say. You
                  can still post; this is what the app expects to happen.
                </p>
              </div>
            )}

            {refusal && (
              <div className="card error" data-testid="post-refused">
                <p>
                  <strong>Nothing was posted.</strong>
                </p>
                {/* The server's own words: they say whether the count moved out of
                    review, whether a line appeared, or that this is not your count. */}
                <p className="error-text">{refusal}</p>
                <p className="muted small">
                  Nothing reached the posting codeunit, so nothing moved. Fix what this
                  names, reload the review, and confirm again.
                </p>
              </div>
            )}

            <div className="confirm__buttons">
              <button
                type="button"
                className="primary"
                disabled={post.isPending}
                data-testid="confirm-post-yes"
                onClick={() => void send()}
              >
                {post.isPending
                  ? "Posting — do not close this…"
                  : `Yes, post ${lineCount} line${lineCount === 1 ? "" : "s"}`}
              </button>
              <button
                type="button"
                className="ghost"
                disabled={post.isPending}
                onClick={() => setOpen(false)}
              >
                Not yet
              </button>
            </div>

            {post.isPending && (
              <p className="muted small" data-testid="post-in-flight">
                Sent. Waiting for Business Central to answer — this is not re-sent if it
                takes a while, and closing the app now will not stop it.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
